import { yupResolver } from "@hookform/resolvers/yup";
import queryString from "query-string";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";

import api from "../../../utils/api";
import { getErrorContext } from "../../../utils/authErrorHandler";
import { getFullName } from "../../../utils/helper";
// Google Auth imports commented out - not ready yet
// import {
//   getAuthRedirectPath,
//   storeSocialAuthTokens,
// } from "../../../utils/socialAuthHelpers";
// import GoogleAuthButton from "../shared/GoogleOAuth/GoogleAuthButton";
import { Logo } from "../shared/Logo";
import {
  AuthCard,
  AuthContainer,
  AuthNavLink,
  AuthSection,
  AuthText,
  ButtonSection,
  ErrorMessage,
  FormSection,
  HeaderSection,
  InputContainer,
  InputGroup,
  InputSection,
  PasswordInputContainer,
  PasswordToggle,
  PrimaryButton,
  ServerErrorAlert,
  StyledInput,
  Subtitle,
  Title,
} from "../shared/authStyles";
import { EyeIcon, EyeSlashIcon } from "../shared/icons/AuthIcons";
import { AnimatePresence, motion } from "../shared/motionUtils";
import { loginSchema } from "../shared/validationSchemas";

const ForgotPasswordLink = styled(AuthNavLink)`
  width: fit-content;
  align-self: flex-end;
  color: #353a46;
  font-family: "Mona Sans", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  text-align: right;

  &:hover {
    color: #353a46;
    text-decoration: underline;
    transform: translateY(-1px);
  }
  &:focus {
    color: #353a46;
  }
`;

const LoginLoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Login = ({
  login: loginRequest,
  auth,
  socialAuth,
  loginRedirectReset,
  loginErrorReset,
  googleAuthReset,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [redirectLocation, setRedirectLocation] = useState(null);
  const [serverError, setServerError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoginLoaderOpen, setIsLoginLoaderOpen] = useState(false);
  const [fromLocation, setFromLocation] = useState(null);
  const passwordRef = useRef(null);
  const emailRef = useRef(null);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting, isSubmitted },
    clearErrors,
  } = useForm({
    resolver: yupResolver(loginSchema),
    mode: "onSubmit",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  useEffect(() => {
    return () => {
      loginErrorReset();
    };
  }, []);

  // Check if user is already logged in and handle redirects
  useEffect(() => {
    let token = localStorage.getItem("access_token");
    let redirect;
    if (location.search) {
      redirect = queryString.parse(location.search);
      setRedirectLocation(redirect);

      // Set email from URL parameter if present
      if (redirect.email) {
        setValue("email", redirect.email);
      }
    }
    if (token != null) {
      setIsLoginLoaderOpen(true);
      if (redirect && redirect.redirect !== null) {
        history.push(`/user/${redirect.redirect}`);
      } else {
        if (window.innerWidth < 992) {
          document.documentElement.classList.remove("nav-open");
        }
        // OnboardingWrapper will handle onboarding check automatically
        // Just redirect to dashboard - no localStorage needed
        history.push("/user/dashboard");
        setIsLoginLoaderOpen(false);
      }
    }
    if (location.state && location.state.from) {
      setFromLocation(location.state.from);
    }
    return () => {
      setFromLocation(null);
    };
  }, [location.search, setValue, history]);

  // Auto-focus email field on component mount
  useEffect(() => {
    if (emailRef.current && !isLoginLoaderOpen) {
      emailRef.current.focus();
    }
  }, [isLoginLoaderOpen]);

  // Handle authentication success and token storage
  useEffect(() => {
    if (auth?.redirect) {
      if (auth.notVerified) {
        const params = `?email=${encodeURIComponent(auth.auth.email)}&name=${
          auth.auth.user_profile.first_name
        }`;
        history.push({
          pathname: "/auth/verify-code",
          search: params,
          state: { from: "login" },
        });
        loginRedirectReset();
      } else if (auth.auth.user_profile !== "undefined") {
        // Store all the necessary tokens and user data
        localStorage.setItem("access_token", auth.auth.token);
        localStorage.setItem("user_id", auth.auth.user_id);
        localStorage.setItem("user_profile", auth.auth.user_profile.id);
        localStorage.setItem(
          "user_profile_color",
          auth.auth.user_profile.color,
        );
        localStorage.setItem(
          "user_fullName",
          getFullName(auth.auth.user_profile),
        );
        localStorage.setItem("email", auth.auth.email);
        localStorage.setItem("created_at", auth.auth.created_at);

        if (auth.auth.is_first_login) {
          localStorage.setItem("is_first_login", "true");
        }

        if (
          auth.auth.user_profile.timezone &&
          auth.auth.user_profile.timezone.tz
        ) {
          localStorage.setItem(
            "user_timezone",
            JSON.stringify(auth.auth.user_profile.timezone),
          );
        }

        if (auth.auth.organizations) {
          auth.auth.organizations.map(organization => {
            if (
              organization.role === "owner" &&
              organization.is_active === true
            ) {
              localStorage.setItem("is_owner", true);
            }
          });
        }

        if (redirectLocation?.redirect) {
          history.push(`/user/${redirectLocation.redirect}`);
        } else {
          if (window.innerWidth < 992) {
            document.documentElement.classList.remove("nav-open");
          }
          history.push("/user/dashboard");
        }

        loginRedirectReset();
      } else {
        history.push("/user/user-profile");
        loginRedirectReset();
      }
    }
  }, [auth, history, loginRedirectReset, redirectLocation]);

  // Handle server errors
  useEffect(() => {
    if (auth?.errorMessage) {
      const errorContext = getErrorContext(auth?.errorMessage);
      if (errorContext.field === "generic") {
        setServerError(errorContext.message);
        setFieldErrors({});
      } else {
        setFieldErrors({ [errorContext.field]: errorContext.message });
        setServerError(null);
      }
    }
  }, [auth?.errorMessage]);

  // Handle Google OAuth authentication success
  // Commented out - Google Auth not ready yet
  // useEffect(() => {
  //   if (socialAuth?.redirect && socialAuth?.auth) {
  //     storeSocialAuthTokens(socialAuth.auth);
  //     const redirectPath = getAuthRedirectPath(
  //       socialAuth.auth,
  //       redirectLocation,
  //     );

  //     // Close mobile navigation if applicable
  //     if (window.innerWidth < 992) {
  //       document.documentElement.classList.remove("nav-open");
  //     }

  //     // Redirect to appropriate page
  //     history.push(redirectPath);
  //     googleAuthReset();
  //   }
  // }, [socialAuth, history, redirectLocation, googleAuthReset]);

  // useEffect(() => {
  //   if (socialAuth?.errorMessage) {
  //     setServerError(socialAuth.errorMessage);
  //   }
  // }, [socialAuth?.errorMessage]);

  const onSubmit = useCallback(
    data => {
      setServerError(null);
      setFieldErrors({});
      const payload = {
        username: data.email,
        password: data.password,
      };
      loginRequest(payload);
    },
    [loginRequest],
  );

  const toggleShowPassword = useCallback(() => {
    setShowPassword(prev => !prev);
    // Keep focus on password field
    setTimeout(() => {
      if (passwordRef.current) {
        passwordRef.current.focus();
      }
    }, 0);
  }, []);

  const handleEmailChange = useCallback(
    onChange => e => {
      // Clear server errors when user starts typing
      if (fieldErrors.email) {
        setFieldErrors(prev => ({ ...prev, email: null }));
      }
      if (serverError) {
        setServerError(null);
      }
      onChange(e);
    },
    [fieldErrors.email, serverError],
  );

  const handlePasswordChange = useCallback(
    onChange => e => {
      // Clear server errors when user starts typing
      if (fieldErrors.password) {
        setFieldErrors(prev => ({ ...prev, password: null }));
      }
      if (serverError) {
        setServerError(null);
      }
      onChange(e);
    },
    [fieldErrors.password, serverError],
  );

  // Show login loader if already logged in
  if (isLoginLoaderOpen) {
    return (
      <LoginLoaderContainer>
        <AuthCard
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "3px solid #f3f3f3",
                borderTop: "3px solid #2684ff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ color: "#6b7280", fontSize: "14px" }}>
              Logging you in...
            </p>
          </div>
        </AuthCard>
      </LoginLoaderContainer>
    );
  }

  return (
    <AuthContainer>
      <AuthCard
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
      >
        <HeaderSection marginBottom="0">
          <Logo />
          <Title margin="0">Login your account</Title>
        </HeaderSection>

        <FormSection as="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <InputSection>
            <InputContainer>
              <InputGroup>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <StyledInput
                      {...field}
                      ref={emailRef}
                      onChange={handleEmailChange(field.onChange)}
                      type="email"
                      placeholder="Email address"
                      autoComplete="email"
                      hasError={
                        (!!errors.email && isSubmitted) || !!fieldErrors.email
                      }
                    />
                  )}
                />
                <AnimatePresence>
                  {fieldErrors.email ? (
                    <ErrorMessage
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {fieldErrors.email}
                    </ErrorMessage>
                  ) : (
                    errors.email &&
                    isSubmitted && (
                      <ErrorMessage
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {errors.email.message}
                      </ErrorMessage>
                    )
                  )}
                </AnimatePresence>
              </InputGroup>

              <InputGroup>
                <PasswordInputContainer>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <StyledInput
                        {...field}
                        onChange={handlePasswordChange(field.onChange)}
                        ref={passwordRef}
                        type={showPassword ? "text" : "password"}
                        placeholder="Password (Min 8 characters)"
                        autoComplete="current-password"
                        hasError={
                          (!!errors.password && isSubmitted) ||
                          !!fieldErrors.password
                        }
                      />
                    )}
                  />
                  <PasswordToggle type="button" onClick={toggleShowPassword}>
                    {showPassword ? (
                      <EyeSlashIcon size="24px" color="#6b7280" />
                    ) : (
                      <EyeIcon size="24px" />
                    )}
                  </PasswordToggle>
                </PasswordInputContainer>
                <AnimatePresence>
                  {fieldErrors.password ? (
                    <ErrorMessage
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      {fieldErrors.password}
                    </ErrorMessage>
                  ) : (
                    errors.password &&
                    isSubmitted && (
                      <ErrorMessage
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        {errors.password.message}
                      </ErrorMessage>
                    )
                  )}
                </AnimatePresence>
              </InputGroup>
            </InputContainer>

            <ForgotPasswordLink to="/auth/forgot-password">
              Forgot password?
            </ForgotPasswordLink>
          </InputSection>

          <AnimatePresence>
            {serverError && (
              <ServerErrorAlert
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {serverError}
              </ServerErrorAlert>
            )}
          </AnimatePresence>

          <ButtonSection direction="column" gap="16px">
            <PrimaryButton
              type="submit"
              disabled={isSubmitting || auth?.isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {auth?.isLoading ? "Signing In..." : "Login"}
            </PrimaryButton>

            {/* <GoogleAuthButton
              text="Login with Google"
              loginHint={control?._formValues?.email}
            /> */}
          </ButtonSection>

          <AuthSection>
            <AuthText>Don't have an account?</AuthText>
            <AuthNavLink to="/auth/register">Sign Up</AuthNavLink>
          </AuthSection>
        </FormSection>
      </AuthCard>
    </AuthContainer>
  );
};

export default Login;
